import { z } from 'zod';
import { ulidSchema } from './common.js';

/**
 * Tasks — the per-game work board, and the changelog derived from it.
 *
 * The board is To Do / Doing / Done / Blocked, and the thing that makes it more than a Trello
 * clone is the other half of this file: cutting a release CLAIMS every finished task, which is
 * what turns "what did we do" into release notes nobody had to write.
 *
 * Everything here is wire shape plus pure logic, shared by the dev app and the Worker. No I/O,
 * no framework imports — both halves of a rule that decides what is on the board have to be the
 * same code, or the two surfaces drift.
 */

/* --------------------------------------------------------------------- status */

/**
 * The four board columns, in display order. This array IS the column order — the board and the
 * agent prompt both iterate it, so a reorder here reorders every surface at once.
 *
 * `blocked` sits last rather than beside `doing` on purpose: it is an exception state, and
 * putting it at the end keeps the normal left-to-right flow (todo → doing → done) unbroken.
 */
export const TASK_STATUSES = ['todo', 'doing', 'done', 'blocked'] as const;
export const taskStatusSchema = z.enum(TASK_STATUSES);
export type TaskStatus = z.infer<typeof taskStatusSchema>;

export const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  todo: 'To Do',
  doing: 'Doing',
  done: 'Done',
  blocked: 'Blocked',
};

export function isTaskStatus(value: unknown): value is TaskStatus {
  return typeof value === 'string' && (TASK_STATUSES as readonly string[]).includes(value);
}

/**
 * Where a task came from. Purely informational, but it is what lets the board say "nine of these
 * came out of a forty-second voice note" honestly.
 */
export const TASK_SOURCES = ['manual', 'voice', 'agent'] as const;
export const taskSourceSchema = z.enum(TASK_SOURCES);
export type TaskSource = z.infer<typeof taskSourceSchema>;

export function isTaskSource(value: unknown): value is TaskSource {
  return typeof value === 'string' && (TASK_SOURCES as readonly string[]).includes(value);
}

/* --------------------------------------------------------------------- limits */

export const MAX_TASK_TITLE_LENGTH = 200;
export const MAX_TASK_DESCRIPTION_LENGTH = 10_000;
export const MAX_TASKS_PER_GAME = 5_000;

/**
 * How many tasks one voice note may produce. A long ramble should become a reviewable list, not
 * fifty cards nobody asked for.
 */
export const MAX_TASKS_PER_VOICE_CAPTURE = 20;

/**
 * Cap on a single voice upload. Roughly ten minutes of Opus/webm at a typical bitrate, and
 * comfortably under the transcription API's own 25 MB request limit.
 */
export const MAX_VOICE_CLIP_BYTES = 20 * 1024 * 1024;

/**
 * Cap on the transcript handed to extraction.
 *
 * Enforced separately from the clip size because the user can EDIT the transcript before
 * approving it — the text that goes to the model is whatever they typed, not necessarily what
 * came back from transcription, so the audio limit no longer bounds it.
 */
export const MAX_TRANSCRIPT_LENGTH = 20_000;

/**
 * One page of the archive. Big enough that a sprint's worth of closed work arrives in a single
 * request, small enough that a game with two years of history doesn't ship all of it to open a
 * panel.
 */
export const ARCHIVE_PAGE_SIZE = 50;
export const MAX_ARCHIVE_PAGE_SIZE = 200;

/** How many tasks may ride in one generated agent prompt. Past this a person cannot review it. */
export const MAX_TASKS_PER_AGENT_PROMPT = 50;

/**
 * Gap between consecutive `orderIndex` values when a column is (re)numbered.
 *
 * Order is a float so a drop between two neighbours is `(a + b) / 2` — one row updated per drag
 * instead of renumbering the column. The gap is large so repeated midpoint splits stay exactly
 * representable for far longer than any real board will last; `needsReindex` below catches the
 * pathological case where they don't.
 */
export const TASK_ORDER_GAP = 1024;

/* ----------------------------------------------------------------- wire shapes */

/**
 * The release that shipped a task.
 *
 * Present on archive reads, where "which build finished this" is the point of the row, and null
 * for a card someone put away by hand.
 */
export interface TaskReleaseLink {
  releaseId: string;
  /** The version label the release was cut as, e.g. "1.0.3". */
  version: string;
  releasedAt: number;
}

export interface Task {
  id: string;
  gameId: string;
  /**
   * Per-game short number. This is the handle humans and coding agents use: `#12` in a note,
   * `#12` in the generated prompt.
   */
  number: number;
  title: string;
  /** Plain text. May be empty. */
  description: string;
  status: TaskStatus;
  orderIndex: number;
  assigneeId: string | null;
  assigneeName: string | null;
  assigneeAvatarUrl: string | null;
  createdById: string | null;
  createdByName: string | null;
  source: TaskSource;
  createdAt: number;
  updatedAt: number;
  doneAt: number | null;
  /**
   * When the card left the board.
   *
   * Archiving is ORTHOGONAL to status: an archived task keeps its column, its release link and
   * its place in every changelog it already earned. It is simply not on the board any more.
   * Null means it still is.
   */
  archivedAt: number | null;
  /**
   * Who archived it by hand. Null when a release archived it, which is the normal case — the
   * build that ships a task is what takes it off the board.
   */
  archivedByName: string | null;
  /** The release that shipped this task, or null when nothing has yet. */
  release?: TaskReleaseLink | null;
}

export const createTaskBodySchema = z.object({
  title: z.string().trim().min(1).max(MAX_TASK_TITLE_LENGTH),
  description: z.string().max(MAX_TASK_DESCRIPTION_LENGTH).default(''),
  status: taskStatusSchema.default('todo'),
  assigneeId: ulidSchema.nullable().default(null),
  source: taskSourceSchema.default('manual'),
});
export type CreateTaskBody = z.infer<typeof createTaskBodySchema>;

export const updateTaskBodySchema = z
  .object({
    title: z.string().trim().min(1).max(MAX_TASK_TITLE_LENGTH).optional(),
    description: z.string().max(MAX_TASK_DESCRIPTION_LENGTH).optional(),
    status: taskStatusSchema.optional(),
    assigneeId: ulidSchema.nullable().optional(),
    /**
     * Take the card off the board (true) or put it back (false).
     *
     * Restoring appends it to the tail of its column rather than restoring its old rank: the
     * column may have been renumbered while the card was away, and a stale float can collide
     * with a live one.
     */
    archived: z.boolean().optional(),
    /**
     * Appended to the description as a dated line. How an agent explains a `blocked` without
     * being able to rewrite what a person wrote.
     */
    note: z.string().trim().min(1).max(2000).optional(),
    /**
     * A drop is described by its NEIGHBOURS, never by a rank the client computed: the server
     * owns the arithmetic, so two people dragging at once cannot disagree about where a card
     * landed. Both null with a `status` means "append to that column".
     */
    beforeId: ulidSchema.nullable().optional(),
    afterId: ulidSchema.nullable().optional(),
  })
  .refine((body) => Object.keys(body).length > 0, { message: 'nothing to update' });
export type UpdateTaskBody = z.infer<typeof updateTaskBodySchema>;

export const archiveQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(MAX_ARCHIVE_PAGE_SIZE).default(ARCHIVE_PAGE_SIZE),
  offset: z.coerce.number().int().min(0).default(0),
});

export interface ListTasksResponse {
  /** The BOARD — archived cards are not in here. */
  tasks: Task[];
  /**
   * How many archived cards the game has, so the board's Archive button can say so without a
   * second round trip on every poll.
   */
  archivedCount: number;
}

/** One page of the archive, newest archived first. */
export interface ArchivedTasksResponse {
  tasks: Task[];
  /** Total archived on the game, not the length of this page. */
  total: number;
}

/**
 * Someone a task can be assigned to.
 *
 * The roster is "everyone who can open this game's board" — admins reach every game implicitly,
 * a dev needs a `game_devs` row. Assigning outside that set would address work to a person who
 * can never see it, so the server re-checks on write.
 */
export interface TaskAssignee {
  id: string;
  name: string;
  avatarUrl: string | null;
}

/** One task the extraction model proposed, before a human accepted it. */
export interface ProposedTask {
  title: string;
  description: string;
  status: TaskStatus;
}

/* ------------------------------------------------------------------- ordering */

/**
 * The `orderIndex` for a card dropped between `before` and `after` within a column. Pass `null`
 * for either end.
 *
 * Pure so the board and the server compute the same number for the same drop, and so the
 * arithmetic is unit-testable without a DOM.
 */
export function orderIndexBetween(before: number | null, after: number | null): number {
  if (before == null && after == null) return TASK_ORDER_GAP;
  if (before == null) return (after as number) - TASK_ORDER_GAP;
  if (after == null) return before + TASK_ORDER_GAP;
  return (before + after) / 2;
}

/**
 * True when repeated midpoint splits have collapsed two neighbours to the point where a float
 * can no longer separate them, and the column must be renumbered.
 *
 * Without this check a board dragged thousands of times in the same spot eventually produces two
 * rows with an identical index, and their relative order starts flipping between reads — the
 * classic fractional-ranking failure.
 */
export function needsReindex(before: number | null, after: number | null): boolean {
  if (before == null || after == null) return false;
  return Math.abs(after - before) < 1e-6;
}

/** Renumber a column onto clean gaps. Returns the new index per task id. */
export function reindexColumn(taskIds: string[]): { id: string; orderIndex: number }[] {
  return taskIds.map((id, i) => ({ id, orderIndex: (i + 1) * TASK_ORDER_GAP }));
}

/* -------------------------------------------------------------------- archive */

/**
 * What an update does to a card's archived state.
 *
 * Pure, and shared, because this is the rule that decides whether a card is on the board at all,
 * and it is reached from three directions: a person clicking Archive, an agent moving a card,
 * and a release claiming everything it shipped.
 *
 * Three things are load-bearing:
 *
 *   - **Archiving an archived card is a no-op**, not a re-archive. The archive is ordered and
 *     paged on the timestamp, so re-archiving a card that is already away would shuffle it back
 *     to the top of the list weeks after it shipped.
 *   - **A status change on an archived card RESTORES it.** Moving something back to Doing is the
 *     claim "this is live work again", and the alternative is a write that reports success and
 *     changes nothing anyone can see.
 *   - **An explicit `archived` always wins** over that inference. A caller that says what it
 *     wants gets what it asked for.
 */
export type ArchiveTransition = 'archive' | 'restore' | 'none';

export function archiveTransition(args: {
  /** What the caller explicitly asked for. `undefined` means they said nothing. */
  archived: boolean | undefined;
  /** True when this same update moves the card to a different column. */
  statusChanged: boolean;
  /** True when the card is currently off the board. */
  isArchived: boolean;
}): ArchiveTransition {
  if (args.archived === true) return args.isArchived ? 'none' : 'archive';
  if (args.archived === false) return args.isArchived ? 'restore' : 'none';
  if (args.statusChanged && args.isArchived) return 'restore';
  return 'none';
}

/* ------------------------------------------------------------------ changelog */

export interface ChangelogEntry {
  number: number;
  title: string;
  /**
   * Plain text. These notes land in a Steam build description, an itch.io patch-notes box and a
   * GOOD BUILD release page, and none of those parse markup.
   */
  description: string;
}

export interface ChangelogResponse {
  /**
   * The release this one follows, or null when it is the first. Shown so the notes can say what
   * range they cover rather than asking anyone to trust them.
   */
  previousRelease: { id: string; version: string; releasedAt: number } | null;
  entries: ChangelogEntry[];
  /**
   * True once the release has been cut and the set is frozen. A preview (`false`) still moves as
   * people finish work; a claimed changelog never changes again.
   */
  frozen: boolean;
}

/**
 * Render a changelog as release-notes text.
 *
 * Plain Markdown: this string is pasted into a store's patch-notes box, written to a GOOD BUILD
 * release and read in a terminal, none of which parse anything richer. One renderer, so the
 * preview in the dev app and the notes that actually ship can never disagree.
 */
export function renderChangelogMarkdown(
  changelog: ChangelogResponse,
  opts: { version?: string } = {},
): string {
  const lines: string[] = [];
  if (opts.version) lines.push(`## ${opts.version}`, '');

  if (changelog.entries.length === 0) {
    lines.push('_No finished tasks since the last release._');
  } else {
    for (const entry of changelog.entries) lines.push(`- **${entry.title}** (#${entry.number})`);
  }

  return lines.join('\n');
}

/* ------------------------------------------------------- the release ledger */

/**
 * What a publish did, per store.
 *
 * Steam and Epic can never put a build in front of players from a command line, so their honest
 * terminal state is `uploaded` and nothing here should grow a field implying otherwise. itch and
 * GOOD BUILD can go live, and `wentLive` records whether this run actually did — a property of
 * the run, never of the config, which is free to change afterwards.
 */
export const PUBLISH_TARGETS = ['steam', 'epic', 'itch', 'goodbuild'] as const;
export const publishTargetSchema = z.enum(PUBLISH_TARGETS);
export type PublishTarget = z.infer<typeof publishTargetSchema>;

export const recordPublishBodySchema = z.object({
  target: publishTargetSchema,
  /** Whatever the store calls the thing that landed: a Steam BuildID, an Epic BuildVersion. */
  externalId: z.string().max(200).nullable().default(null),
  wentLive: z.boolean().default(false),
  platform: z.string().max(40),
  configuration: z.string().max(40),
});

export const recordReleaseBodySchema = z.object({
  version: z.string().trim().min(1).max(64),
  /** Markdown, usually the rendered changelog the dev app showed before publishing. */
  notes: z.string().max(50_000).default(''),
  publish: recordPublishBodySchema,
});
export type RecordReleaseBody = z.infer<typeof recordReleaseBodySchema>;

export interface ReleasePublish {
  id: string;
  target: PublishTarget;
  externalId: string | null;
  wentLive: boolean;
  platform: string;
  configuration: string;
  publishedAt: number;
  publishedByName: string | null;
}

export interface Release {
  id: string;
  gameId: string;
  version: string;
  notes: string;
  createdAt: number;
  /** How many tasks this release claimed. The changelog itself is fetched separately. */
  taskCount: number;
  publishes: ReleasePublish[];
}

export const releaseListQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(20),
});
